import React, { FC, useCallback, useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Alert,
    Box,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import InfoText from 'components/shared/info-text.component';
import AddStepAutocomplete, {
    StepOption,
} from 'components/shared/add-step-autocomplete/add-step-autocomplete.component';
import { useDepartments } from 'context/departments-context';
import { useProductionValueSteps } from 'hooks/useProductionValueSteps';
import { ProductionValueTemplate } from 'models/organization';

type ValueTemplateStep = {
    _id: string;
    name: string;
    valuePercent: number | '';
};

type ValueTemplateStepsProps = {
    productionTemplate: ProductionValueTemplate | null;
    onStepsChange?: (steps: ValueTemplateStep[], isValid: boolean) => void;
};

const ValueTemplateSteps: FC<ValueTemplateStepsProps> = ({ productionTemplate, onStepsChange }) => {
    const { t: translate } = useTranslation(['lab-settings']);
    const { departments } = useDepartments();
    const { productionValueSteps } = useProductionValueSteps();

    const getInitialSteps = (template: ProductionValueTemplate | null): ValueTemplateStep[] => {
        return (template?.steps || []).map((step) => ({
            _id: step.stepId || step._id || '',
            name: step.name ?? '',
            valuePercent: step.valuePercent ?? 0,
        }));
    };

    const [selectedSteps, setSelectedSteps] = useState<ValueTemplateStep[]>(() => getInitialSteps(productionTemplate));

    useEffect(() => {
        if (productionTemplate?.steps?.length) {
            setSelectedSteps(getInitialSteps(productionTemplate));
        }
    }, [productionTemplate]);

    const totalPercent = useMemo(
        () =>
            selectedSteps.reduce(
                (sum, step) => sum + (typeof step.valuePercent === 'number' ? step.valuePercent : 0),
                0,
            ),
        [selectedSteps],
    );

    const notify = useCallback(
        (steps: ValueTemplateStep[]) => {
            const total = steps.reduce(
                (sum, step) => sum + (typeof step.valuePercent === 'number' ? step.valuePercent : 0),
                0,
            );
            const allNonNegative = steps.every((step) => Number(step.valuePercent) >= 0);
            onStepsChange?.(steps, total === 100 && allNonNegative);
        },
        [onStepsChange],
    );

    const handleAddStep = useCallback(
        (selected: StepOption) => {
            if (selectedSteps.some((step) => step._id === selected.id)) return;
            const source = productionValueSteps.find((step) => step._id === selected.id);
            if (!source) return;
            const updated = [...selectedSteps, { _id: source._id ?? '', name: source.name, valuePercent: 0 }];
            setSelectedSteps(updated);
            notify(updated);
        },
        [selectedSteps, productionValueSteps, notify],
    );

    const handleDeleteStep = useCallback(
        (stepId: string) => {
            const updated = selectedSteps.filter((step) => step._id !== stepId);
            setSelectedSteps(updated);
            notify(updated);
        },
        [selectedSteps, notify],
    );

    const handleValuePercentChange = useCallback(
        (index: number, value: string) => {
            const parsed = value === '' ? '' : Math.max(0, parseInt(value, 10) || 0);
            const updated = [...selectedSteps];
            updated[index] = { ...updated[index], valuePercent: parsed };
            setSelectedSteps(updated);
            notify(updated);
        },
        [selectedSteps, notify],
    );

    const handleDragEnd = useCallback(
        (result: DropResult) => {
            if (!result.destination) return;
            const reordered = [...selectedSteps];
            const [removed] = reordered.splice(result.source.index, 1);
            reordered.splice(result.destination.index, 0, removed);
            setSelectedSteps(reordered);
            notify(reordered);
        },
        [selectedSteps, notify],
    );

    const getDepartmentName = useCallback(
        (stepId: string) => {
            const valueStep = productionValueSteps.find((s) => s._id === stepId);
            if (!valueStep) return '';
            return departments.find((dept) => dept._id === valueStep.departmentId)?.name ?? '';
        },
        [productionValueSteps, departments],
    );

    const stepOptions: StepOption[] = useMemo(
        () =>
            productionValueSteps
                .filter((step) => !selectedSteps.some((selected) => selected._id === step._id))
                .map((step) => ({
                    id: step._id ?? '',
                    name: step.name,
                    departmentName: departments.find((dept) => dept._id === step.departmentId)?.name ?? '',
                })),
        [productionValueSteps, selectedSteps, departments],
    );

    return (
        <Box>
            <Paper variant="outlined">
                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="value-template-steps-droppable">
                        {(provided) => (
                            <Table size="small" ref={provided.innerRef} {...provided.droppableProps}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell colSpan={2}>
                                            {translate('lab-settings:PRODUCTION_VALUE_STEPS')}
                                        </TableCell>
                                        <TableCell sx={{ width: 130 }}>
                                            {translate('lab-settings:VALUE_PERCENT')}
                                        </TableCell>
                                        <TableCell sx={{ width: 20 }} />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {selectedSteps.map((step, index) => (
                                        <Draggable key={step._id} draggableId={step._id} index={index}>
                                            {(p, s) => (
                                                <TableRow
                                                    ref={p.innerRef}
                                                    {...p.draggableProps}
                                                    style={{
                                                        ...p.draggableProps.style,
                                                        display: s.isDragging ? 'table' : 'table-row',
                                                        background: s.isDragging ? '#f3f3f3' : undefined,
                                                    }}
                                                >
                                                    <TableCell {...p.dragHandleProps} sx={{ width: 0, minWidth: 0 }}>
                                                        <DragIndicatorIcon />
                                                    </TableCell>
                                                    <TableCell>
                                                        {getDepartmentName(step._id)} - {step.name}
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            size="small"
                                                            type="number"
                                                            value={step.valuePercent}
                                                            onChange={(e) =>
                                                                handleValuePercentChange(index, e.target.value)
                                                            }
                                                            inputProps={{ step: 1, min: 0 }}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <IconButton onClick={() => handleDeleteStep(step._id)}>
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                    {!selectedSteps.length && (
                                        <TableRow>
                                            <TableCell colSpan={4}>
                                                <InfoText text={translate('lab-settings:NO_STEPS_ADDED')} />
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        )}
                    </Droppable>
                </DragDropContext>
            </Paper>

            {selectedSteps.length > 0 && totalPercent !== 100 && (
                <Box mt={1} textAlign="right">
                    <Alert severity="error">
                        {translate('lab-settings:TOTAL_VALUE_MUST_BE_100_PERCENT')} ({totalPercent}%)
                    </Alert>
                </Box>
            )}

            <Box mt={2}>
                {stepOptions.length ? (
                    <AddStepAutocomplete options={stepOptions} onStepSelected={handleAddStep} />
                ) : (
                    <InfoText text={translate('lab-settings:ALL_WORKFLOW_STEPS_ADDED')} />
                )}
            </Box>
        </Box>
    );
};

ValueTemplateSteps.displayName = 'ValueTemplateSteps';
export default ValueTemplateSteps;
